/**
 * Converts the URL returned by the Blinkl matcher (`data.blinkl.data.url`)
 * into a player token { iv, content, layout } usable by the existing
 * ads-player playlist API, WITHOUT navigating away from the SPA.
 *
 * The Blinkl URL points to adp-api and returns a 302 Location to
 * `${PLAYER}/play/{in|tt}?iv=...&content=...`. We perform a manual-redirect
 * fetch and parse `iv` / `content` from the Location header.
 */

export class PlayTokenError extends Error {
  constructor(code, cause) {
    super(code);
    this.code = code;
    if (cause) this.cause = cause;
  }
}

const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);

export async function resolvePlayToken(blinklUrl) {
  if (!blinklUrl) throw new PlayTokenError('missing_url');

  let response;
  try {
    response = await fetch(blinklUrl, {
      method: 'GET',
      redirect: 'manual',
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch (e) {
    throw new PlayTokenError('network_error', e);
  }

  // Some browsers expose status 0 + type 'opaqueredirect' when redirect:manual
  // is used on a cross-origin response — we can't read Location in that case.
  if (response.type === 'opaqueredirect' || response.status === 0) {
    throw new PlayTokenError('opaque_redirect');
  }

  if (response.status === 404 || response.status === 410) {
    throw new PlayTokenError('link_expired');
  }

  if (!REDIRECT_STATUSES.has(response.status)) {
    throw new PlayTokenError('unexpected_status');
  }

  const location = response.headers.get('Location');
  if (!location) throw new PlayTokenError('missing_location');

  let target;
  try {
    target = new URL(location, blinklUrl);
  } catch (e) {
    throw new PlayTokenError('invalid_location', e);
  }

  const iv = target.searchParams.get('iv');
  const content = target.searchParams.get('content');
  if (!iv || !content) throw new PlayTokenError('missing_token');

  const layout = target.pathname.includes('/tt') ? 'tt' : 'in';
  return { iv, content, layout };
}
